import { Button, Modal, Box, TextField, InputAdornment, Fade, Backdrop } from "@mui/material";
import Grid from "@mui/material/Grid";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import StoreRoundedIcon from "@mui/icons-material/StoreRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import LocationCityRoundedIcon from "@mui/icons-material/LocationCityRounded";
import MarkunreadMailboxRoundedIcon from "@mui/icons-material/MarkunreadMailboxRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import FreeBreakfastRoundedIcon from "@mui/icons-material/FreeBreakfastRounded";
import { format } from "date-fns";

// ---- shared palette (matches PatientRegistrationForm / PatientTable) ----
const palette = {
  teal: "#4B49AC",
  tealDark: "#37357D",
  tealSoft: "#EEEDF9",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
  coral: "#FF6B57",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    fontSize: "14px",
    "& fieldset": { borderColor: palette.line },
    "&:hover fieldset": { borderColor: palette.teal },
    "&.Mui-focused fieldset": { borderColor: palette.teal, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "14px",
    "&.Mui-focused": { color: palette.teal },
  },
  "& .MuiInputAdornment-root svg": {
    fontSize: "19px",
    color: palette.slate,
  },
};

const timeFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    fontSize: "13.5px",
    "& fieldset": { borderColor: palette.line },
    "&:hover fieldset": { borderColor: palette.teal },
    "&.Mui-focused fieldset": { borderColor: palette.teal, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "13px",
    "&.Mui-focused": { color: palette.teal },
  },
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
    color: palette.slate,
  },
};

const SectionLabel = ({ icon, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "14px" }}>
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: palette.tealSoft,
        color: palette.teal,
        "& svg": { fontSize: "16px" },
      }}
    >
      {icon}
    </Box>
    <span
      style={{
        fontSize: "12px",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: palette.slate,
      }}
    >
      {children}
    </span>
  </Box>
);

const BranchCreationForm = ({
  open,
  handleClose,
  formData,
  setFormData,
  handleInputChange,
  handleCreateBranch,
  validationErrors,
  isEditClicked,
}) => {
  const handleTimeChange = (field) => (newValue) =>
    setFormData((prev) => ({
      ...prev,
      [field]: newValue ? format(newValue, "HH:mm:ss") : null,
    }));

  const timeValue = (val) => (val ? new Date(`1970-01-01T${val}`) : null);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 300, sx: { backgroundColor: "rgba(17, 24, 39, 0.55)" } } }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "860px",
            maxWidth: "94vw",
            maxHeight: "88vh",
            overflowY: "auto",
            bgcolor: palette.bg,
            borderRadius: "18px",
            boxShadow: "0 24px 60px -12px rgba(15, 23, 42, 0.35)",
            outline: "none",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              display: "flex",
              alignItems: "center",
              gap: "14px",
              padding: "22px 28px",
              background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.tealDark} 100%)`,
              borderRadius: "18px 18px 0 0",
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                backgroundColor: "rgba(255,255,255,0.16)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <StoreRoundedIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <h3 style={{ margin: 0, color: "#fff", fontSize: "18px", fontWeight: 700 }}>
                {isEditClicked ? "Edit Branch" : "Create a New Branch"}
              </h3>
              <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.75)", fontSize: "12.5px" }}>
                {isEditClicked
                  ? "Update the branch's details below"
                  : "Enter the branch details and operating hours to get started"}
              </p>
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{
                color: "#fff",
                backgroundColor: "rgba(255,255,255,0.12)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ padding: "26px 28px 28px" }}>
            {/* Branch details */}
            <SectionLabel icon={<StoreRoundedIcon />}>Branch Details</SectionLabel>
            <Grid container spacing={2} sx={{ mb: "26px" }}>
              <Grid size={12}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label="Branch Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  autoComplete="off"
                  sx={fieldSx}
                  error={!!validationErrors.name}
                  helperText={validationErrors.name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <StoreRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  required
                  fullWidth
                  multiline
                  maxRows={4}
                  size="small"
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  autoComplete="off"
                  sx={fieldSx}
                  error={!!validationErrors.address}
                  helperText={validationErrors.address}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: "10px" }}>
                        <LocationOnRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Location */}
            <SectionLabel icon={<PublicRoundedIcon />}>Location</SectionLabel>
            <Grid container spacing={2} sx={{ mb: "26px" }}>
              <Grid size={6}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  autoComplete="off"
                  sx={fieldSx}
                  error={!!validationErrors.country}
                  helperText={validationErrors.country}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PublicRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  autoComplete="off"
                  sx={fieldSx}
                  error={!!validationErrors.city}
                  helperText={validationErrors.city}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationCityRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label="Postal Code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleInputChange}
                  autoComplete="off"
                  sx={fieldSx}
                  error={!!validationErrors.postal_code}
                  helperText={validationErrors.postal_code}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MarkunreadMailboxRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label="Contact Number"
                  name="contact_number"
                  value={formData.contact_number}
                  onChange={handleInputChange}
                  autoComplete="off"
                  sx={fieldSx}
                  error={!!validationErrors.contact_number}
                  helperText={validationErrors.contact_number}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CallRoundedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Operating hours */}
            <SectionLabel icon={<ScheduleRoundedIcon />}>Operating Hours</SectionLabel>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <TimePicker
                    label="Start Time *"
                    value={timeValue(formData?.start_time)}
                    onChange={handleTimeChange("start_time")}
                    renderInput={(params) => (
                      <TextField fullWidth size="small" sx={timeFieldSx} {...params} />
                    )}
                  />
                </Grid>
                <Grid size={6}>
                  <TimePicker
                    label="End Time *"
                    value={timeValue(formData?.close_time)}
                    onChange={handleTimeChange("close_time")}
                    renderInput={(params) => (
                      <TextField fullWidth size="small" sx={timeFieldSx} {...params} />
                    )}
                  />
                </Grid>
                <Grid size={6}>
                  <TimePicker
                    label="Break Start Time *"
                    value={timeValue(formData?.break_start_time)}
                    onChange={handleTimeChange("break_start_time")}
                    renderInput={(params) => (
                      <TextField fullWidth size="small" sx={timeFieldSx} {...params} />
                    )}
                  />
                </Grid>
                <Grid size={6}>
                  <TimePicker
                    label="Break End Time *"
                    value={timeValue(formData?.break_end_time)}
                    onChange={handleTimeChange("break_end_time")}
                    renderInput={(params) => (
                      <TextField fullWidth size="small" sx={timeFieldSx} {...params} />
                    )}
                  />
                </Grid>
              </Grid>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  mt: "10px",
                  color: palette.slate,
                  fontSize: "12px",
                }}
              >
                <FreeBreakfastRoundedIcon sx={{ fontSize: "15px" }} />
                Break hours are excluded from bookable slots automatically.
              </Box>
            </LocalizationProvider>

            {/* Footer */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                mt: "30px",
                pt: "20px",
                borderTop: `1px solid ${palette.line}`,
              }}
            >
              <Button
                className="create_form_btn"
                variant="outlined"
                onClick={handleClose}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  borderColor: palette.line,
                  color: palette.slate,
                  padding: "8px 22px",
                  "&:hover": { borderColor: palette.slate, backgroundColor: "#F3F4F6" },
                }}
              >
                Cancel
              </Button>
              <Button
                className="create_form_btn"
                variant="contained"
                onClick={handleCreateBranch}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "10px",
                  padding: "8px 24px",
                  backgroundColor: palette.teal,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: palette.tealDark, boxShadow: "none" },
                }}
              >
                {isEditClicked ? "Save Changes" : "Create Branch"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default BranchCreationForm;
